import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, TextField, Paper, Chip, Avatar,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, Collapse, Tooltip, Divider,
  LinearProgress, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import {
  Users, Play, Copy, Clock, LogOut, Send, ChevronDown, ChevronUp,
  RotateCcw, UserPlus, Hash, Award, CheckCircle,
  XCircle, Target, BookOpen,
} from 'lucide-react';
import { useStudyRoom, VIEWS, QUICK_REACTIONS } from '../../contexts/StudyRoomContext';
import { useAuth } from '../../contexts/AuthContext';

const GRADES = [
  { value: 10, label: 'Grade 10' },
  { value: 11, label: 'Grade 11' },
  { value: 12, label: 'Grade 12' },
];
const DURATIONS = [
  { value: 900, label: '15 min' },
  { value: 1500, label: '25 min' },
  { value: 1800, label: '30 min' },
  { value: 2700, label: '45 min' },
  { value: 3600, label: '60 min' },
];

function fmt(s) {
  return `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;
}
function timerClr(r, t) {
  const p = t > 0 ? r / t : 1;
  return p > 0.5 ? '#4caf50' : p > 0.2 ? '#ff9800' : '#f44336';
}
function Dot({ status }) {
  const c = { active: '#4caf50', idle: '#ff9800', disconnected: '#f44336' };
  return <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c[status] || c.active, display: 'inline-block', mr: 0.5 }} />;
}

// ---- HOME ----
function HomeView() {
  const { user } = useAuth();
  const { joinQueue, createRoom, joinRoom, fetchStatus, fetchHistory, usage, history, isPro, loading, error, clearError } = useStudyRoom();
  const [joinCode, setJoinCode] = useState('');
  const [grade, setGrade] = useState(user?.profile?.grade || 10);
  const [dur, setDur] = useState(1500);

  useEffect(() => { fetchStatus(); fetchHistory(1, 5); }, [fetchStatus, fetchHistory]);

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Users size={32} color="#fff" />
          </Box>
        </Box>
        <Typography variant="h5" fontWeight={700} gutterBottom>Study Together, Focus Better</Typography>
        <Typography variant="body2" color="text.secondary">Find a study partner and stay focused with timed sessions.</Typography>
      </Box>

      {error && <Alert severity="warning" onClose={clearError} sx={{ mb: 2 }}>{error}</Alert>}

      {!isPro && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">Sessions this week</Typography>
            <Chip label={`${usage.sessionsUsed} / ${usage.sessionsLimit}`} size="small" color={usage.canStart ? 'default' : 'error'} />
          </Box>
          <LinearProgress variant="determinate" value={(usage.sessionsUsed / usage.sessionsLimit) * 100} sx={{ mt: 1, borderRadius: 1 }} />
          {!usage.canStart && <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>Weekly limit reached. Upgrade to Pro for unlimited sessions.</Typography>}
        </Paper>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <FormControl size="small" fullWidth>
          <InputLabel>Grade</InputLabel>
          <Select value={grade} label="Grade" onChange={(e) => setGrade(e.target.value)}>
            {GRADES.map(g => <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel>Duration</InputLabel>
          <Select value={dur} label="Duration" onChange={(e) => setDur(e.target.value)}>
            {DURATIONS.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <Button variant="contained" fullWidth size="large" startIcon={<UserPlus size={20} />} onClick={() => joinQueue({ grade, duration: dur })} disabled={loading || !usage.canStart} sx={{ mb: 2, py: 1.5, borderRadius: 3, fontWeight: 600 }}>
        {loading ? 'Searching...' : 'Find a Study Partner'}
      </Button>
      <Button variant="outlined" fullWidth startIcon={<Hash size={18} />} onClick={() => createRoom({ grade, duration: dur })} disabled={loading || !usage.canStart} sx={{ mb: 3, borderRadius: 3 }}>
        Create a Room
      </Button>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>Join by Code</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField size="small" placeholder="Enter room code" value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} inputProps={{ maxLength: 10, style: { textTransform: 'uppercase', letterSpacing: 2, fontWeight: 600 } }} fullWidth onKeyDown={(e) => e.key === 'Enter' && joinCode.trim().length >= 4 && joinRoom(joinCode.trim())} />
          <Button variant="contained" onClick={() => joinRoom(joinCode.trim())} disabled={joinCode.trim().length < 4 || loading}>Join</Button>
        </Box>
      </Paper>

      {history.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Clock size={16} /> Recent Sessions</Typography>
          {history.map((s, i) => (
            <Paper key={s._id || i} sx={{ p: 2, mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>{s.duration ? `${Math.round(s.duration / 60)} min session` : 'Study Session'}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.participants?.length || 0} participants • {s.createdAt ? new Date(s.createdAt).toLocaleDateString() : ''}</Typography>
                </Box>
                <Typography variant="body2" color="primary" fontWeight={600}>{s.questionsAnswered || 0} Qs</Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ---- QUEUE ----
function QueueView() {
  const { leaveQueue, queueStartTime } = useStudyRoom();
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = queueStartTime || Date.now();
    const iv = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [queueStartTime]);

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', p: 3, textAlign: 'center', mt: 8 }}>
      <Box sx={{ position: 'relative', display: 'inline-flex', mb: 3 }}>
        <CircularProgress size={100} thickness={2} />
        <Box sx={{ position: 'absolute', top: 0, left: 0, bottom: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Users size={36} />
        </Box>
      </Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>Finding a Study Partner...</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Matching you with someone in your grade</Typography>
      <Typography variant="h4" fontWeight={700} color="primary" sx={{ mb: 3 }}>{fmt(elapsed)}</Typography>
      {elapsed >= 60 && <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>Taking longer than usual. You can keep waiting, create a room, or try again later.</Alert>}
      <Button variant="outlined" color="error" onClick={leaveQueue} startIcon={<XCircle size={18} />} sx={{ borderRadius: 3 }}>Cancel</Button>
    </Box>
  );
}

// ---- LOBBY ----
function LobbyView() {
  const { roomCode, participants, startSession, leaveRoom, loading } = useStudyRoom();
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const isHost = participants.length > 0 && participants[0]?.userId === user?._id;

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(roomCode); } catch { /* fallback */ }
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box sx={{ maxWidth: 500, mx: 'auto', p: 3, textAlign: 'center', mt: 4 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>Study Room Lobby</Typography>
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'action.hover' }}>
        <Typography variant="caption" color="text.secondary">Room Code</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 1 }}>
          <Typography variant="h3" fontWeight={800} letterSpacing={4} fontFamily="monospace">{roomCode}</Typography>
          <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
            <IconButton onClick={copyCode} size="small"><Copy size={20} /></IconButton>
          </Tooltip>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Share this code with your study partner</Typography>
      </Paper>

      <Typography variant="subtitle2" sx={{ mb: 2, textAlign: 'left' }}>Participants ({participants.length})</Typography>
      {participants.map((p, i) => (
        <Paper key={p.userId || i} sx={{ p: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: i === 0 ? 'primary.main' : 'secondary.main', width: 36, height: 36 }}>{(p.username || 'U')[0].toUpperCase()}</Avatar>
          <Box sx={{ flex: 1, textAlign: 'left' }}>
            <Typography variant="body2" fontWeight={600}>{p.username || 'Student'}{p.userId === user?._id && ' (You)'}</Typography>
            <Typography variant="caption" color="text.secondary">Grade {p.grade || '?'}</Typography>
          </Box>
          {i === 0 && <Chip label="Host" size="small" color="primary" variant="outlined" />}
        </Paper>
      ))}
      {participants.length < 2 && <Box sx={{ py: 3 }}><CircularProgress size={24} sx={{ mb: 1 }} /><Typography variant="body2" color="text.secondary">Waiting for partner to join...</Typography></Box>}

      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button variant="outlined" color="error" onClick={leaveRoom} startIcon={<LogOut size={18} />} sx={{ flex: 1, borderRadius: 3 }}>Leave</Button>
        {isHost && <Button variant="contained" startIcon={<Play size={18} />} onClick={startSession} disabled={participants.length < 2 || loading} sx={{ flex: 1, borderRadius: 3 }}>Start Session</Button>}
      </Box>
    </Box>
  );
}

// ---- SESSION ----
function SessionView() {
  const { timeRemaining, totalDuration, participants, messages, activityStats, sendMessage, sendReaction, leaveRoom } = useStudyRoom();
  const { user } = useAuth();
  const [chatOpen, setChatOpen] = useState(false);
  const [msgInput, setMsgInput] = useState('');
  const [confirmLeave, setConfirmLeave] = useState(false);
  const chatEnd = useRef(null);

  const color = timerClr(timeRemaining, totalDuration);
  const progress = totalDuration > 0 ? ((totalDuration - timeRemaining) / totalDuration) * 100 : 0;
  const partner = participants.find(p => p.userId !== user?._id);

  useEffect(() => { if (chatOpen && chatEnd.current) chatEnd.current.scrollIntoView({ behavior: 'smooth' }); }, [messages, chatOpen]);

  const doSend = () => { if (msgInput.trim()) { sendMessage(msgInput); setMsgInput(''); } };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h2" fontWeight={800} fontFamily="monospace" sx={{ color, transition: 'color 0.5s' }}>{fmt(timeRemaining)}</Typography>
        <LinearProgress variant="determinate" value={progress} sx={{ mt: 1, height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { bgcolor: color, transition: 'background-color 0.5s' } }} />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Paper sx={{ flex: 1, minWidth: 140, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><Dot status="active" /><Typography variant="body2" fontWeight={600}>You</Typography></Box>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Box sx={{ textAlign: 'center' }}><Typography variant="h6" fontWeight={700} color="primary">{activityStats.questionsAnswered}</Typography><Typography variant="caption" color="text.secondary">Answered</Typography></Box>
            <Box sx={{ textAlign: 'center' }}><Typography variant="h6" fontWeight={700} color="success.main">{activityStats.correctAnswers}</Typography><Typography variant="caption" color="text.secondary">Correct</Typography></Box>
          </Box>
        </Paper>
        {partner && (
          <Paper sx={{ flex: 1, minWidth: 140, p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><Dot status={partner.status || 'active'} /><Typography variant="body2" fontWeight={600}>{partner.username || 'Partner'}</Typography></Box>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Box sx={{ textAlign: 'center' }}><Typography variant="h6" fontWeight={700} color="primary">{partner.questionsAnswered || 0}</Typography><Typography variant="caption" color="text.secondary">Answered</Typography></Box>
              <Box sx={{ textAlign: 'center' }}><Typography variant="h6" fontWeight={700} color="success.main">{partner.correctAnswers || 0}</Typography><Typography variant="caption" color="text.secondary">Correct</Typography></Box>
            </Box>
          </Paper>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
        {QUICK_REACTIONS.map(r => (
          <Tooltip key={r.key} title={r.label}><Button size="small" variant="outlined" onClick={() => sendReaction(r.key)} sx={{ minWidth: 'auto', px: 1.5, borderRadius: 5, fontSize: '1.2rem' }}>{r.emoji}</Button></Tooltip>
        ))}
      </Box>

      <Button fullWidth variant="text" onClick={() => setChatOpen(!chatOpen)} endIcon={chatOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />} sx={{ mb: 1, justifyContent: 'space-between', textTransform: 'none' }}>
        Chat {messages.length > 0 && `(${messages.length})`}
      </Button>
      <Collapse in={chatOpen}>
        <Paper sx={{ p: 2, maxHeight: 250, overflow: 'auto', mb: 2 }}>
          {messages.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>No messages yet. Say hi!</Typography>}
          {messages.map((m, i) => (
            <Box key={i} sx={{ mb: 1, p: 1, borderRadius: 2, bgcolor: m.userId === user?._id ? 'primary.main' : 'action.hover', color: m.userId === user?._id ? 'primary.contrastText' : 'text.primary', ml: m.userId === user?._id ? 4 : 0, mr: m.userId === user?._id ? 0 : 4 }}>
              {m.isReaction ? <Typography variant="body1" sx={{ textAlign: 'center', fontSize: '1.5rem' }}>{QUICK_REACTIONS.find(r => r.key === m.reactionType)?.emoji || m.reactionType}</Typography> : (
                <><Typography variant="caption" fontWeight={600}>{m.userId === user?._id ? 'You' : m.username || 'Partner'}</Typography><Typography variant="body2">{m.message}</Typography></>
              )}
            </Box>
          ))}
          <div ref={chatEnd} />
        </Paper>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField size="small" fullWidth placeholder="Type a message..." value={msgInput} onChange={(e) => setMsgInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doSend()} inputProps={{ maxLength: 200 }} />
          <IconButton color="primary" onClick={doSend} disabled={!msgInput.trim()}><Send size={20} /></IconButton>
        </Box>
      </Collapse>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button variant="outlined" color="error" size="small" startIcon={<LogOut size={16} />} onClick={() => setConfirmLeave(true)}>Leave Session</Button>
      </Box>
      <Dialog open={confirmLeave} onClose={() => setConfirmLeave(false)}>
        <DialogTitle>Leave Session?</DialogTitle>
        <DialogContent><Typography variant="body2">Your progress will be saved, but the session will end for you.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmLeave(false)}>Stay</Button>
          <Button color="error" onClick={() => { setConfirmLeave(false); leaveRoom(); }}>Leave</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ---- SUMMARY ----
function SummaryView() {
  const { sessionSummary, resetRoom, joinQueue } = useStudyRoom();
  const s = sessionSummary || {};
  const dur = s.duration ? Math.round(s.duration / 60) : 0;

  return (
    <Box sx={{ maxWidth: 500, mx: 'auto', p: 3, textAlign: 'center', mt: 4 }}>
      <Box sx={{ mb: 3 }}><Award size={48} color="#4caf50" /></Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Session Complete!</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Great job staying focused for {dur} minutes</Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Paper sx={{ flex: 1, minWidth: 120, p: 2 }}><Target size={24} color="#2196f3" /><Typography variant="h5" fontWeight={700} sx={{ mt: 1 }}>{s.questionsAnswered || 0}</Typography><Typography variant="caption" color="text.secondary">Questions</Typography></Paper>
        <Paper sx={{ flex: 1, minWidth: 120, p: 2 }}><CheckCircle size={24} color="#4caf50" /><Typography variant="h5" fontWeight={700} sx={{ mt: 1 }}>{s.correctAnswers || 0}</Typography><Typography variant="caption" color="text.secondary">Correct</Typography></Paper>
        <Paper sx={{ flex: 1, minWidth: 120, p: 2 }}><Clock size={24} color="#ff9800" /><Typography variant="h5" fontWeight={700} sx={{ mt: 1 }}>{dur}</Typography><Typography variant="caption" color="text.secondary">Minutes</Typography></Paper>
      </Box>
      {s.partner && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Partner Stats</Typography>
          <Divider sx={{ mb: 1 }} />
          <Typography variant="body2" fontWeight={600}>{s.partner.username || 'Partner'}</Typography>
          <Typography variant="caption" color="text.secondary">{s.partner.questionsAnswered || 0} Qs • {s.partner.correctAnswers || 0} correct</Typography>
        </Paper>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Button variant="contained" startIcon={<RotateCcw size={18} />} onClick={() => joinQueue()} sx={{ borderRadius: 3, py: 1.2 }}>Find New Partner</Button>
        <Button variant="outlined" startIcon={<BookOpen size={18} />} onClick={resetRoom} sx={{ borderRadius: 3 }}>Back to Study Rooms</Button>
      </Box>
    </Box>
  );
}

// ---- MAIN ----
export default function StudyRooms() {
  const { view } = useStudyRoom();
  switch (view) {
    case VIEWS.QUEUE: return <QueueView />;
    case VIEWS.LOBBY: return <LobbyView />;
    case VIEWS.SESSION: return <SessionView />;
    case VIEWS.SUMMARY: return <SummaryView />;
    default: return <HomeView />;
  }
}
